<template>
  <a-modal
    :title="$t('telegramMessage.messageDetail')"
    :visible="visible"
    :footer="null"
    width="960px"
    wrap-class-name="telegram-detail-modal"
    @cancel="$emit('close')"
  >
    <div v-if="record" class="telegram-detail">
      <div class="telegram-detail-hero">
        <div class="telegram-detail-icon">
          <i class="fab fa-telegram-plane" />
        </div>
        <div class="telegram-detail-heading">
          <div class="telegram-detail-title-row">
            <h4>{{ record.title || '-' }}</h4>
            <span :class="['telegram-status-badge', statusClass(record.status)]">{{ statusLabel(record.status) }}</span>
          </div>
          <div class="telegram-detail-meta">
            <span><i class="fas fa-users" /> {{ audienceLabel }}</span>
            <span><i class="far fa-clock" /> {{ formatDateTime(record.created_at) }}</span>
            <span><i class="fas fa-user" /> {{ record.created_by_name || '-' }}</span>
          </div>
        </div>
      </div>

      <div class="telegram-detail-stats">
        <div v-for="item in statusSummary" :key="item.status" class="telegram-stat-card">
          <span>{{ item.label }}</span>
          <strong>{{ item.count }}</strong>
        </div>
      </div>

      <div class="telegram-detail-main">
        <section class="telegram-message-card">
          <div class="telegram-section-header">
            <span>{{ $t('telegramMessage.content') }}</span>
          </div>
          <p>{{ record.content || '-' }}</p>
        </section>

        <section class="telegram-media-card">
          <div class="telegram-section-header">
            <span>{{ $t('telegramMessage.image') }}</span>
          </div>
          <div v-if="record.image_path" class="telegram-image-frame">
            <img :src="record.image_path" :alt="record.title">
          </div>
          <div v-else class="telegram-empty-media">
            <i class="far fa-image" />
            <span>-</span>
          </div>
        </section>
      </div>

      <section class="telegram-recipient-card">
        <div class="telegram-section-header telegram-recipient-header">
          <span>{{ $t('telegramMessage.recipientDelivery') }}</span>
          <strong>{{ totalRecipients }}</strong>
        </div>
        <div v-if="loading" class="telegram-recipient-loading">
          <a-spin />
          <strong>{{ $t('telegramMessage.loadingRecipients') }}</strong>
          <span>{{ $t('telegramMessage.loadingRecipientsHint') }}</span>
        </div>
        <div v-else>
          <div class="telegram-recipient-table-wrap">
            <table class="telegram-recipient-table">
            <thead>
              <tr>
                <th>{{ $t('telegramMessage.recipientType') }}</th>
                <th>{{ $t('telegramMessage.telegramChatId') }}</th>
                <th>{{ $t('telegramMessage.status') }}</th>
                <th>{{ $t('telegramMessage.sentAt') }}</th>
                <th>{{ $t('telegramMessage.errorMessage') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="recipient in recipients" :key="recipient.id">
                <td>
                  <div class="telegram-recipient-type">
                    <span :class="['telegram-type-dot', 'telegram-type-' + recipient.recipient_type]" />
                    <div>
                      <strong>{{ recipient.recipient_name || recipient.recipient_id }}</strong>
                      <small>{{ recipientTypeLabel(recipient.recipient_type) }} - {{ recipient.recipient_id }}</small>
                    </div>
                  </div>
                </td>
                <td class="telegram-chat-cell">{{ recipient.telegram_chat_id }}</td>
                <td><span :class="['telegram-status-badge', statusClass(recipient.status)]">{{ statusLabel(recipient.status) }}</span></td>
                <td>{{ formatDateTime(recipient.sent_at) }}</td>
                <td>
                  <span v-if="recipient.error_message" class="telegram-error-message">{{ recipient.error_message }}</span>
                  <span v-else class="telegram-muted">-</span>
                </td>
              </tr>
              <tr v-if="!recipients.length">
                <td colspan="5">
                  <div class="telegram-empty-row">
                    <i class="far fa-paper-plane" />
                    <span>{{ $t('telegramMessage.noRecipients') }}</span>
                  </div>
                </td>
              </tr>
            </tbody>
            </table>
          </div>
          <div v-if="hasRecipientPages" class="telegram-recipient-pagination">
            <button type="button" :disabled="currentPage <= 1 || loading" @click="$emit('page-change', currentPage - 1)">
              {{ $t('telegramMessage.previousPage') }}
            </button>
            <span>{{ currentPage }} / {{ lastPage }}</span>
            <button type="button" :disabled="currentPage >= lastPage || loading" @click="$emit('page-change', currentPage + 1)">
              {{ $t('telegramMessage.nextPage') }}
            </button>
          </div>
        </div>
      </section>
    </div>
  </a-modal>
</template>

<script>
import moment from 'moment'

export default {
  name: 'TelegramMessageDetailModal',
  props: {
    visible: { type: Boolean, default: false },
    record: { type: Object, default: null },
    loading: { type: Boolean, default: false }
  },
  computed: {
    recipients () {
      return this.record && this.record.recipients ? this.record.recipients : []
    },
    recipientsMeta () {
      return this.record && this.record.recipients_meta ? this.record.recipients_meta : {}
    },
    totalRecipients () {
      if (typeof this.recipientsMeta.total !== 'undefined') return this.recipientsMeta.total
      if (this.record && typeof this.record.recipients_count !== 'undefined') return this.record.recipients_count
      return this.recipients.length
    },
    currentPage () {
      return this.recipientsMeta.current_page || 1
    },
    lastPage () {
      return this.recipientsMeta.last_page || 1
    },
    hasRecipientPages () {
      return this.lastPage > 1
    },
    audienceLabel () {
      if (!this.record) return '-'
      const labels = {
        shop: this.$t('telegramMessage.shop'),
        driver: this.$t('telegramMessage.driver'),
        all: this.$t('telegramMessage.allRecipients')
      }

      return labels[this.record.audience_type] || this.record.audience_type
    },
    statusSummary () {
      const statuses = ['pending', 'sent', 'failed']
      const counts = this.record && this.record.recipients_status_counts
        ? this.record.recipients_status_counts
        : this.recipients.reduce((carry, recipient) => {
          const status = recipient.status === 'completed' ? 'sent' : recipient.status
          carry[status] = (carry[status] || 0) + 1
          return carry
        }, {})

      return statuses.map(status => ({
        status,
        label: this.statusLabel(status),
        count: counts[status] || 0
      }))
    }
  },
  methods: {
    recipientTypeLabel (type) {
      const labels = {
        shop: this.$t('telegramMessage.shop'),
        driver: this.$t('telegramMessage.driver')
      }

      return labels[type] || type
    },
    statusClass (status) {
      return `telegram-status-${status}`
    },
    statusLabel (status) {
      return ({
        draft: this.$t('telegramMessage.draft'),
        scheduled: this.$t('telegramMessage.scheduled'),
        pending: this.$t('telegramMessage.sending'),
        completed: this.$t('telegramMessage.sent'),
        sent: this.$t('telegramMessage.sent'),
        failed: this.$t('telegramMessage.failed'),
        partially_failed: this.$t('telegramMessage.partiallyFailed')
      })[status] || status
    },
    formatDateTime (value) {
      return value ? moment(value).format('DD-MM-YYYY HH:mm') : '-'
    }
  }
}
</script>

<style scoped>
.telegram-detail { color: #0f172a; }
.telegram-detail-hero { align-items: flex-start; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; display: flex; gap: 12px; margin-bottom: 14px; padding: 14px; }
.telegram-detail-icon { align-items: center; background: #e0f2fe; border: 1px solid #bae6fd; border-radius: 8px; color: #0284c7; display: flex; flex: 0 0 42px; height: 42px; justify-content: center; }
.telegram-detail-heading { flex: 1; min-width: 0; }
.telegram-detail-title-row { align-items: flex-start; display: flex; gap: 10px; justify-content: space-between; }
.telegram-detail-title-row h4 { color: #0f172a; font-size: 17px; font-weight: 700; line-height: 1.35; margin: 0; overflow-wrap: anywhere; }
.telegram-detail-meta { color: #64748b; display: flex; flex-wrap: wrap; font-size: 12px; gap: 8px 16px; margin-top: 8px; }
.telegram-detail-meta i { color: #94a3b8; margin-right: 5px; }
.telegram-detail-stats { display: grid; gap: 10px; grid-template-columns: repeat(3, minmax(0, 1fr)); margin-bottom: 14px; }
.telegram-stat-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; }
.telegram-stat-card span { color: #64748b; display: block; font-size: 11px; font-weight: 700; text-transform: uppercase; }
.telegram-stat-card strong { color: #0f172a; display: block; font-size: 22px; line-height: 1.1; margin-top: 4px; }
.telegram-detail-main { display: grid; gap: 14px; grid-template-columns: minmax(0, 1fr) 260px; margin-bottom: 14px; }
.telegram-message-card, .telegram-media-card, .telegram-recipient-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
.telegram-section-header { align-items: center; background: #f8fafc; border-bottom: 1px solid #e2e8f0; color: #334155; display: flex; font-size: 12px; font-weight: 700; justify-content: space-between; padding: 9px 12px; text-transform: uppercase; }
.telegram-recipient-header strong { background: #e0f2fe; border-radius: 999px; color: #0369a1; font-size: 11px; padding: 3px 8px; }
.telegram-message-card p { font-size: 13px; line-height: 1.65; margin: 0; max-height: 220px; overflow: auto; padding: 12px; white-space: pre-line; }
.telegram-image-frame { align-items: center; background: #f8fafc; display: flex; height: 220px; justify-content: center; padding: 10px; }
.telegram-image-frame img { border-radius: 6px; max-height: 200px; max-width: 100%; object-fit: contain; }
.telegram-empty-media { align-items: center; color: #94a3b8; display: flex; flex-direction: column; gap: 6px; height: 220px; justify-content: center; }
.telegram-empty-media i { font-size: 26px; }
.telegram-recipient-loading { align-items: center; color: #64748b; display: flex; flex-direction: column; gap: 8px; justify-content: center; min-height: 180px; padding: 28px; text-align: center; }
.telegram-recipient-loading strong { color: #0f172a; font-size: 13px; margin-top: 4px; }
.telegram-recipient-loading span { color: #64748b; font-size: 12px; }
.telegram-recipient-table-wrap { max-height: 420px; overflow: auto; }
.telegram-recipient-pagination { align-items: center; background: #f8fafc; border-top: 1px solid #e2e8f0; display: flex; gap: 10px; justify-content: flex-end; padding: 10px 12px; }
.telegram-recipient-pagination button { background: #fff; border: 1px solid #cbd5e1; border-radius: 6px; color: #334155; font-size: 12px; padding: 5px 10px; }
.telegram-recipient-pagination button:disabled { color: #94a3b8; cursor: not-allowed; opacity: .65; }
.telegram-recipient-pagination span { color: #64748b; font-size: 12px; font-weight: 600; }
.telegram-recipient-table { border-collapse: separate; border-spacing: 0; min-width: 860px; width: 100%; }
.telegram-recipient-table th, .telegram-recipient-table td { border-bottom: 1px solid #e2e8f0; font-size: 12px; padding: 10px 12px; vertical-align: middle; }
.telegram-recipient-table th { background: #fff; color: #64748b; font-size: 11px; font-weight: 700; position: sticky; text-align: left; text-transform: uppercase; top: 0; white-space: nowrap; z-index: 1; }
.telegram-recipient-table tbody tr:last-child td { border-bottom: 0; }
.telegram-recipient-table tbody tr:hover { background: #f8fafc; }
.telegram-recipient-type { align-items: center; display: flex; gap: 9px; min-width: 160px; }
.telegram-recipient-type strong { color: #0f172a; display: block; font-size: 12px; line-height: 1.2; }
.telegram-recipient-type small { color: #64748b; display: block; font-size: 11px; margin-top: 2px; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.telegram-type-dot { border-radius: 50%; display: inline-block; flex: 0 0 9px; height: 9px; width: 9px; }
.telegram-type-shop { background: #0ea5e9; }
.telegram-type-driver { background: #22c55e; }
.telegram-chat-cell { color: #334155; font-family: monospace; max-width: 180px; overflow-wrap: anywhere; }
.telegram-error-message { color: #b91c1c; display: inline-block; max-width: 300px; overflow-wrap: anywhere; }
.telegram-muted { color: #94a3b8; }
.telegram-empty-row { align-items: center; color: #94a3b8; display: flex; flex-direction: column; gap: 6px; justify-content: center; padding: 24px; }
.telegram-status-badge { border-radius: 999px; display: inline-block; font-size: 11px; font-weight: 700; line-height: 1.2; padding: 5px 10px; text-transform: capitalize; white-space: nowrap; }
.telegram-status-draft { background: #f1f5f9; color: #475569; }
.telegram-status-scheduled { background: #ede9fe; color: #6d28d9; }
.telegram-status-pending { background: #fef3c7; color: #92400e; }
.telegram-status-completed, .telegram-status-sent { background: #dcfce7; color: #166534; }
.telegram-status-failed { background: #fee2e2; color: #b91c1c; }
.telegram-status-partially_failed { background: #ffedd5; color: #c2410c; }
@media (max-width: 767px) {
  .telegram-detail-title-row, .telegram-detail-hero { display: block; }
  .telegram-detail-icon { margin-bottom: 10px; }
  .telegram-detail-stats, .telegram-detail-main { grid-template-columns: 1fr; }
}
</style>
